import {
  identifyUser,
  setUserProperties,
  type MixpanelUserProperties,
} from "common/analytics";

/**
 * Local calendar date as `YYYY-MM-DD` — the value for `account_creation_date`.
 * Pass this in `extra` only on account-creation paths (never on plain login),
 * since the account is created at this moment. The backend hides `created_at`,
 * so the client signup date is the source.
 */
export function signupDateToday(): string {
  const d = new Date();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${month}-${day}`;
}

// Loose shape covering the various auth responses (password login / register / social).
type AuthUserLike = {
  id?: string | number | null;
  userId?: string | number | null;
  email?: string | null;
  name?: string | null;
  username?: string | null;
  gender?: string | null;
  year_of_birth?: string | number | null;
  email_verified?: boolean | null;
  // From the /user/login + /user/sync-user response — used to seed the people properties.
  claimed_businesses?: Array<{
    business_id: number | string;
    business_name: string;
  }> | null;
  saved_places_count?: number | null;
  // Comma-separated accessibility abilities (source for accessibility_profile).
  ability?: string | null;
  // Account preference rows; `disability_visible` drives profile_visibility.
  account_preferences?: Array<{
    key?: string | null;
    value?: string | null;
  }> | null;
};

/**
 * Identify the user with the analytics provider and set their user (people) properties.
 * Call on every auth-success path (password login, account creation, social). No-op without an id.
 * `extra` lets a caller add path-specific props (e.g. account_creation_method on signup).
 */
export function applyUserIdentity(
  user: AuthUserLike | null | undefined,
  extra: Partial<MixpanelUserProperties> = {},
): void {
  const id = user?.id ?? user?.userId;
  if (id === undefined || id === null || id === "") return;

  identifyUser(String(id));

  const props: Partial<MixpanelUserProperties> = { account_id: String(id) };
  if (user?.email) props.email = user.email;
  if (user?.name) props.name = user.name;
  if (user?.username) props.username = user.username;
  if (user?.gender) props.gender = user.gender;
  if (user?.year_of_birth !== undefined && user?.year_of_birth !== null && user.year_of_birth !== "") {
    props.year_of_birth = Number(user.year_of_birth);
  }
  if (typeof user?.email_verified === "boolean") props.email_verified = user.email_verified;

  // Seed from the login/sync-user payload (top-level fields). claimed_business_* and
  // saved_places_count then stay current via their own update paths (claim success / saved-places).
  if (Array.isArray(user?.claimed_businesses)) {
    props.claimed_business_id = user.claimed_businesses.map((b) =>
      String(b.business_id),
    );
    props.claimed_business_name = user.claimed_businesses.map(
      (b) => b.business_name,
    );
  }
  if (typeof user?.saved_places_count === "number") {
    props.saved_places_count = user.saved_places_count;
  }

  // accessibility_profile — comma-separated `ability` from the login/sync-user payload, split the
  // same way the edit path (UpdateAccessibilityProfile) reads it, so the seed equals what an edit
  // would write. Seeds at login so it's populated for users who predate this instrumentation or
  // haven't re-edited; the edit path keeps it current afterwards. Raw-response sites lacking
  // `ability` (e.g. fresh register) skip cleanly.
  if (typeof user?.ability === "string" && user.ability.length > 0) {
    props.accessibility_profile = user.ability.split(",");
  }

  // profile_visibility — derived from the `disability_visible` account preference, mirroring
  // isUserDisabilityPublic (no preference OR value "1" => "public", otherwise "private"). The
  // BasicInfo disability toggle keeps it current. Skipped when account_preferences is absent.
  if (Array.isArray(user?.account_preferences)) {
    const pref = user.account_preferences.find(
      (p) => p?.key === "disability_visible",
    );
    props.profile_visibility =
      pref == null || pref.value === "1" ? "public" : "private";
  }

  setUserProperties({ ...props, ...extra });
}
